import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { CMSLayout } from '../layouts/cms/cms-layout';
import { Error } from '../pages/error/error';
import { NotFound } from '../pages/not-found/not-found';
import { LoginLayout } from '../features/users/layouts/login/login.layout';
import { ChangePasswordLoader } from '../features/users/loaders/cms/change-password/change-password.loader';
import { RequireAuth } from '../features/users/components/require-auth/require-auth';
import { CmsLayoutLoader } from './loaders/cms/layout/cms-layout.loader';
import { AdminDeletedCompanyListLoader } from '../features/companies/loaders/cms/admin/admin-deleted-company-list.loader';
import { AdminCompanyEditLoader } from '../features/companies/loaders/cms/admin/admin-company-edit.loader';
import { AdminUserListLoader } from '../features/users/loaders/cms/admin/admin-user-list.loader';
import { AdminUserAddLoader } from '../features/users/loaders/cms/admin/admin-user-add.loader';
import { AdminUserEditLoader } from '../features/users/loaders/cms/admin/admin-user-edit.loader';
import { UserProfileLoader } from '../features/users/loaders/cms/user-profile/user-profile.loader';
import { AdminActiveCompanyListLoader } from '../features/companies/loaders/cms/admin/admin-active-company-list.loader';
import { AdminLicenseEditLoader } from '../features/licenses/loaders/cms/admin/admin-license-edit.loader';
import { AdminDashboard } from '../pages/cms/admin/dashboard/admin-dashboard';
import { CompanyAdminDashboard } from '../pages/cms/company-admin/dashboard/company-admin-dashboard';
import { CompanyAdminUserListLoader } from '../features/users/loaders/cms/company-admin/company-admin-user-list.loader';
import { CompanyAdminUserEditLoader } from '../features/users/loaders/cms/company-admin/company-admin-user-edit.loader';
import { MaintainerDashboard } from '../pages/cms/maintainer/dashboard/maintainer-dashboard';
import { MaintainerUserListLoader } from '../features/users/loaders/cms/maintainer/maintainer-user-list.loader';
import { MaintainerUserEditLoader } from '../features/users/loaders/cms/maintainer/maintainer-user-edit.loader';
import { Login } from '../features/users/pages/cms/login/login';
import { ForgottenPassword } from '../features/users/pages/cms/forgotten-password/forgotten-password';
import { ChangePassword } from '../features/users/pages/cms/change-password/change-password';
import { RoleEnum } from '../features/users/enums/role.enum';
import { UserProfile } from '../features/users/pages/cms/user-profile/user-profile';
import { AdminActiveCompanyList } from '../features/companies/pages/cms/admin/companies/active-list/admin-active-company-list';
import { AdminDeletedCompanyList } from '../features/companies/pages/cms/admin/companies/deleted-list/admin-deleted-company-list';
import { AdminCompanyEdit } from '../features/companies/pages/cms/admin/companies/edit/admin-company-edit';
import { AdminCompanyAdd } from '../features/companies/pages/cms/admin/companies/add/admin-company-add';
import { AdminLicenseAdd } from '../features/licenses/pages/cms/admin/license/add/admin-license-add';
import { AdminLicenseEdit } from '../features/licenses/pages/cms/admin/license/edit/admin-license-edit';
import { AdminUserList } from '../features/users/pages/cms/admin/users/list/admin-user-list';
import { AdminUserAdd } from '../features/users/pages/cms/admin/users/add/admin-user-add';
import { AdminUserEdit } from '../features/users/pages/cms/admin/users/edit/admin-user-edit';
import { CompanyAdminUserList } from '../features/users/pages/cms/company-admin/users/list/company-admin-user-list';
import { CompanyAdminUserAdd } from '../features/users/pages/cms/company-admin/users/add/company-admin-user-add';
import { CompanyAdminUserEdit } from '../features/users/pages/cms/company-admin/users/edit/company-admin-user-edit';
import { CompanyAdminProjectList } from '../features/projects/pages/cms/company-admin/projects/list/company-admin-project-list';
import { CompanyAdminProjectListLoader } from '../features/projects/loaders/cms/company-admin/company-admin-project-list.loader';
import { CompanyAdminProjectAdd } from '../features/projects/pages/cms/company-admin/projects/add/company-admin-project-add';
import { CompanyAdminProjectEdit } from '../features/projects/pages/cms/company-admin/projects/edit/company-admin-project-edit';
import { CompanyAdminProjectEditLoader } from '../features/projects/loaders/cms/company-admin/company-admin-project-edit.loader';
import { CompanyAdminProjectModules } from '../features/projects/pages/cms/company-admin/projects/modules/index/company-admin-project-modules';
import { CompanyAdminProjectModulesLoader } from '../features/projects/loaders/cms/company-admin/modules/company-admin-project-modules';
import { CompanyAdminFileList } from '../features/files/pages/cms/files/list/company-admin-file-list';
import { MaintainerUserList } from '../features/users/pages/cms/maintainer/users/list/maintainer-user-list';
import { MaintainerUserAdd } from '../features/users/pages/cms/maintainer/users/add/maintainer-user-add';
import { MaintainerUserEdit } from '../features/users/pages/cms/maintainer/users/edit/maintainer-user-edit';

export const router = createBrowserRouter([
    {
        path: '',
        errorElement: <Error/>,
        children: [
            {
                path: '',
                element: <Navigate to='/cms/login' />
            },
            {
                path: 'cms',
                element: <Navigate to='/cms/login' />
            },
            {
                path: '',
                element: <LoginLayout />,
                children: [
                    {
                        path: 'cms/login',
                        element: <Login />
                    },
                    {
                        path: 'forgotten-password',
                        element: <ForgottenPassword />
                    },
                    {
                        path: 'change-password/:token',
                        element: <ChangePassword />,
                        loader: ChangePasswordLoader
                    }
                ]
            },
            {
                path: '',
                element: <CMSLayout/>,
                loader: CmsLayoutLoader,
                shouldRevalidate: () => false,
                children: [
                    {
                        path: 'cms/users/profile',
                        element: (
                            <RequireAuth roles={[RoleEnum.Admin, RoleEnum.CompanyAdmin, RoleEnum.Maintainer, RoleEnum.User]}>
                                <UserProfile />
                            </RequireAuth>
                        ),
                        loader: UserProfileLoader
                    },
                    //  ADMIN ROUTES
                    {
                        path: 'cms/admin',
                        element: <RequireAuth roles={[RoleEnum.Admin]}><Outlet/></RequireAuth>,  
                        children: [
                            {
                                path: '',
                                element: <Navigate to='/cms/admin/dashboard' />,
                            },
                            {
                                path: 'dashboard',
                                element: <AdminDashboard />
                            },
                            {
                                path: 'active-companies',
                                element: <AdminActiveCompanyList />,
                                loader: AdminActiveCompanyListLoader
                            },
                            {
                                path: 'deleted-companies',
                                element: <AdminDeletedCompanyList />,
                                loader: AdminDeletedCompanyListLoader
                            },
                            {
                                path: 'companies/edit/:id',
                                element: <AdminCompanyEdit />,
                                loader: AdminCompanyEditLoader
                            },
                            {
                                path: 'companies/add',
                                element: <AdminCompanyAdd />
                            },
                            {
                                path: 'companies/:companyId/licenses/add',
                                element: <AdminLicenseAdd />
                            },
                            {
                                path: 'licenses/edit/:id',
                                element: <AdminLicenseEdit />,
                                loader: AdminLicenseEditLoader
                            },
                            {
                                path: 'users',
                                element: <AdminUserList />,
                                loader: AdminUserListLoader
                            },
                            {
                                path: 'users/add',
                                element: <AdminUserAdd />,
                                loader: AdminUserAddLoader
                            },
                            {
                                path: 'users/edit/:id',
                                element: <AdminUserEdit />,
                                loader: AdminUserEditLoader
                            }
                        ]
                    },
                    // COMPANY ADMIN ROUTES
                    {
                        path: 'cms/company-admin',
                        element: <RequireAuth roles={[RoleEnum.CompanyAdmin]}><Outlet/></RequireAuth>,  
                        children: [
                            {
                                path: '',
                                element: <Navigate to='/cms/company-admin/dashboard' />,
                            },
                            {
                                path: 'dashboard',
                                element: <CompanyAdminDashboard />
                            },
                            {
                                path: 'users',
                                element: <CompanyAdminUserList />,
                                loader: CompanyAdminUserListLoader
                            },
                            {
                                path: 'users/add',
                                element: <CompanyAdminUserAdd />
                            },
                            {
                                path: 'users/edit/:id',
                                element: <CompanyAdminUserEdit />,
                                loader: CompanyAdminUserEditLoader
                            },
                            {
                                path: 'projects',
                                element: <CompanyAdminProjectList />,
                                loader: CompanyAdminProjectListLoader
                            },
                            {
                                path: 'projects/add',
                                element: <CompanyAdminProjectAdd />
                            },
                            {
                                path: 'projects/edit/:id',
                                element: <CompanyAdminProjectEdit />,
                                loader: CompanyAdminProjectEditLoader
                            },
                            {
                                path: 'projects/:id/modules',
                                element: <CompanyAdminProjectModules />,
                                loader: CompanyAdminProjectModulesLoader
                            },
                            {
                                path: 'files',
                                element: <CompanyAdminFileList />
                            }
                        ]
                    },
                    // MAINTAINER ROUTES
                    {
                        path: 'cms/maintainer',
                        element: <RequireAuth roles={[RoleEnum.Maintainer]}><Outlet/></RequireAuth>,  
                        children: [
                            {
                                path: '',
                                element: <Navigate to='/cms/maintainer/dashboard' />,
                            },
                            {
                                path: 'dashboard',
                                element: <MaintainerDashboard />
                            },
                            {
                                path: 'users',
                                element: <MaintainerUserList />,
                                loader: MaintainerUserListLoader
                            },
                            {
                                path: 'users/add',
                                element: <MaintainerUserAdd />
                            },
                            {
                                path: 'users/edit/:id',
                                element: <MaintainerUserEdit />,
                                loader: MaintainerUserEditLoader
                            }
                        ]
                    }
                ]
            }
        ]
    },
    {
        path: '*',
        element: <NotFound />
    }
]);
